import type { LegacyUserDetail } from '../../services/legacyUserManager';
import type {
  LegacyMigratePlanConfiguration,
  LegacyMigratePlanPatient,
} from '../../services/legacyUserManager';

/** Mirror of services mapConfigurationNormalize ROOM_ALIASES (preview fallback). */
const ROOM_ALIASES: Record<string, string> = {
  restaurant: 'restaurant',
  kitchen: 'kitchen',
  bedroom: 'bedroom',
  dining_room: 'dining_room',
  diningroom: 'dining_room',
  ocean: 'ocean',
  oceanmap: 'ocean',
  volcano: 'volcano',
  stadium: 'stadium',
};

const normalizeRoom = (room: unknown): string => {
  const raw = String(room ?? '').trim();
  if (!raw) return '(empty room)';
  const key = raw.toLowerCase().replace(/\s+/g, '_');
  return ROOM_ALIASES[key] ?? ROOM_ALIASES[raw.toLowerCase()] ?? raw;
};

const mapStatus = (status: unknown): string => {
  const s = String(status ?? 'draft').trim().toLowerCase();
  if (s === 'completed' || s === 'complete') return 'completed';
  if (s === 'in-progress' || s === 'in_progress' || s === 'running') {
    return 'in-progress';
  }
  return 'queued';
};

const truthy = (value: unknown): boolean =>
  value === true || value === 1 || value === '1';

const toIsoOrNull = (value: unknown): string | null => {
  if (value == null || value === '') return null;
  return String(value);
};

/**
 * Build patients + configurations drafts from vapor detail when dry-run plan
 * lacks them (staging API not yet deployed with clinical mapping).
 */
export const buildClinicalFromLegacyDetail = (
  detail: LegacyUserDetail
): {
  patients: LegacyMigratePlanPatient[];
  configurations: LegacyMigratePlanConfiguration[];
} => {
  const itemsByList = new Map<number, number[]>();
  const foodNameById = new Map<number, string>();
  for (const row of detail.food_list_food ?? []) {
    const listId = Number(row.food_list_id);
    const foodId = Number(row.food_id);
    if (!Number.isFinite(listId) || !Number.isFinite(foodId)) continue;
    const list = itemsByList.get(listId) ?? [];
    list.push(foodId);
    itemsByList.set(listId, list);
    if (row.food_name != null && String(row.food_name).trim()) {
      foodNameById.set(foodId, String(row.food_name));
    }
  }
  const listNameById = new Map<number, string | null>();
  for (const row of detail.food_lists ?? []) {
    listNameById.set(Number(row.id), row.name == null ? null : String(row.name));
  }

  const patients: LegacyMigratePlanPatient[] = (detail.patients ?? []).map(
    (row) => ({
      vapor_patient_id: Number(row.id),
      code: String(row.code ?? '').trim(),
      created_at: toIsoOrNull(row.created_at),
      updated_at: toIsoOrNull(row.updated_at),
      baseline: row.baseline == null ? null : Number(row.baseline),
      variation: row.variation == null ? null : Number(row.variation),
    })
  );

  const foodResultsByCfg = new Map<number, number>();
  for (const row of detail.food_results ?? []) {
    const cfgId = Number(row.configuration_id);
    if (!Number.isFinite(cfgId)) continue;
    foodResultsByCfg.set(cfgId, (foodResultsByCfg.get(cfgId) ?? 0) + 1);
  }
  const hrByCfg = new Map<number, { count: number; baseline: number | null }>();
  for (const row of detail.a_time_events ?? []) {
    if (String(row.name ?? '').toLowerCase() !== 'heart_rate') continue;
    const cfgId =
      row.configuration_id == null ? NaN : Number(row.configuration_id);
    if (!Number.isFinite(cfgId) || cfgId <= 0) continue;
    const cur = hrByCfg.get(cfgId) ?? { count: 0, baseline: null };
    cur.count += 1;
    if (cur.baseline == null && row.baseline != null) {
      cur.baseline = Number(row.baseline);
    }
    hrByCfg.set(cfgId, cur);
  }

  const configurations: LegacyMigratePlanConfiguration[] = [];
  for (const row of detail.configurations ?? []) {
    if (row.deleted_at != null && row.deleted_at !== '') continue;
    const foodListId =
      row.food_list_id == null || row.food_list_id === ''
        ? null
        : Number(row.food_list_id);
    const scenario = normalizeRoom(row.room);
    const status = mapStatus(row.status);
    const patientId = Number(row.patient_id);
    const deviceId = Number(row.user_device_id);
    const cfgId = Number(row.id);
    const hrMeta = hrByCfg.get(cfgId);

    if (foodListId != null && Number.isFinite(foodListId) && foodListId > 0) {
      const foodIds = itemsByList.get(foodListId) ?? [];
      configurations.push({
        vapor_configuration_id: cfgId,
        vapor_patient_id: patientId,
        vapor_device_id: deviceId,
        type: 'exposure',
        scenario,
        status,
        config: {
          foodIds,
          foodNames: foodIds.map((id) => foodNameById.get(id) ?? `#${id}`),
          buffet: truthy(row.is_buffet),
          is_recording: truthy(row.is_recording),
          vapor_food_list_id: foodListId,
          vapor_food_list_name: listNameById.get(foodListId) ?? null,
        },
        created_at: toIsoOrNull(row.created_at),
        updated_at: toIsoOrNull(row.updated_at),
        food_feedback_count: foodResultsByCfg.get(cfgId) ?? 0,
        hr_sample_count: hrMeta?.count ?? 0,
        baseline: hrMeta?.baseline ?? null,
      });
      continue;
    }

    configurations.push({
      vapor_configuration_id: cfgId,
      vapor_patient_id: patientId,
      vapor_device_id: deviceId,
      type: 'performance',
      scenario,
      status,
      config: {
        hr_min: row.hr_min == null ? null : Number(row.hr_min),
        hr_max: row.hr_max == null ? null : Number(row.hr_max),
        difficulty: row.difficulty == null ? null : Number(row.difficulty),
        start: row.start == null ? 0 : Number(row.start),
        is_recording: truthy(row.is_recording),
      },
      created_at: toIsoOrNull(row.created_at),
      updated_at: toIsoOrNull(row.updated_at),
      food_feedback_count: foodResultsByCfg.get(cfgId) ?? 0,
      hr_sample_count: hrMeta?.count ?? 0,
      baseline: hrMeta?.baseline ?? null,
    });
  }

  return { patients, configurations };
};

export const mergeClinicalIntoPlan = <
  T extends {
    patients?: LegacyMigratePlanPatient[];
    configurations?: LegacyMigratePlanConfiguration[];
    products?: Array<{
      product_id: number;
      until_date: string | null;
      source: 'course' | 'video' | 'exposure' | 'performance';
      vapor_ref_id: number;
    }>;
  },
>(
  plan: T,
  fromDetail: {
    patients: LegacyMigratePlanPatient[];
    configurations: LegacyMigratePlanConfiguration[];
  }
): T & {
  patients: LegacyMigratePlanPatient[];
  configurations: LegacyMigratePlanConfiguration[];
  products: NonNullable<T['products']>;
} => {
  const patients =
    plan.patients && plan.patients.length > 0
      ? plan.patients
      : fromDetail.patients;
  const configurations =
    plan.configurations && plan.configurations.length > 0
      ? plan.configurations
      : fromDetail.configurations;

  const products = [...(plan.products ?? [])];
  const hasProduct = (id: number) => products.some((p) => p.product_id === id);
  const hasExposure = configurations.some((c) => c.type === 'exposure');
  const hasPerformance = configurations.some((c) => c.type === 'performance');
  if (hasExposure && !hasProduct(75)) {
    const ref =
      configurations.find((c) => c.type === 'exposure')?.vapor_configuration_id ??
      0;
    products.push({
      product_id: 75,
      until_date: null,
      source: 'exposure',
      vapor_ref_id: ref,
    });
  }
  if (hasPerformance && !hasProduct(76)) {
    const ref =
      configurations.find((c) => c.type === 'performance')
        ?.vapor_configuration_id ?? 0;
    products.push({
      product_id: 76,
      until_date: null,
      source: 'performance',
      vapor_ref_id: ref,
    });
  }

  return { ...plan, patients, configurations, products };
};
